"use client"

import { useState, useEffect } from "react"
import { Users, LogIn } from 'lucide-react'
import { supabase } from "../../../Supabase/SupabaseClient" // Ajusta la ruta según tu estructura
import Footer from "../../../Components/Footer/Footer";
 import "./hiring.css"

export default function ServiceProviders() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  
  const [formData, setFormData] = useState({
    providerName: "",
    email: "",
    phone: "",
    serviceType: "",
    portfolio: null,
    notes: "",
  })

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: "",
    loading: false,
  })

  // Verificar si el usuario está autenticado
  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        setUser(user)
        
        // Si hay usuario, prellenar el formulario con sus datos
        if (user) {
          setFormData(prev => ({
            ...prev,
            providerName: user.user_metadata?.name || "",
            email: user.email || "",
          }))
        }
      } catch (error) {
        console.error('Error al verificar usuario:', error)
      } finally {
        setLoading(false)
      }
    }

    checkUser()

    // Escuchar cambios en el estado de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          setUser(session.user)
          setFormData(prev => ({
            ...prev,
            providerName: session.user.user_metadata?.name || "",
            email: session.user.email || "",
          }))
        } else {
          setUser(null)
        }
        setLoading(false)
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Si está cargando, mostrar spinner
  if (loading) {
    return (
      <section className="hiring">
        <div className="hiring__container">
          <div className="hiring__loading">
            <div className="hiring__spinner"></div>
            <p>Verificando autenticación...</p>
          </div>
        </div>
      </section>
    )
  }

  // Si no está autenticado, mostrar mensaje de login
  if (!user) {
    return (
      <section className="hiring">
        <div className="hiring__container">
          <div className="hiring__auth-required">
            <LogIn className="hiring__auth-icon" size={60} />
            <h2 className="hiring__auth-title">Inicia Sesión Requerida</h2>
            <p className="hiring__auth-message">
              Para enviar tu solicitud como proveedor, necesitas tener una cuenta y estar autenticado.
            </p>
            <p className="hiring__auth-submessage">
              Por favor, inicia sesión o crea una cuenta para continuar.
            </p>
            <button 
              onClick={() => window.location.href = '/login'} // Ajusta la ruta según tu app
              className="hiring__auth-button"
            >
              Ir a Iniciar Sesión
            </button>
          </div>
        </div>
      </section>
    )
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        portfolio: e.target.files[0],
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validación básica
    if (!formData.providerName || !formData.email || !formData.serviceType) {
      setFormStatus({
        submitted: false,
        error: true,
        message: "Por favor completa todos los campos requeridos.",
        loading: false,
      })
      return
    }

    setFormStatus({ 
      submitted: false, 
      error: false, 
      message: "", 
      loading: true 
    })

    try {
      // Verificar nuevamente que el usuario esté autenticado
      if (!user) {
        setFormStatus({
          submitted: false,
          error: true,
          message: "Debes iniciar sesión para enviar tu solicitud.",
          loading: false,
        })
        return
      }

      // Insertar directamente en Supabase usando el ID del usuario autenticado
      const { data, error } = await supabase
        .from('proveedores')
        .insert([
          {
            id: user.id, // Usar el ID del usuario autenticado
            nombre: formData.providerName,
            email: formData.email,
            telefono: formData.phone || null,
            direccion: formData.notes || null,
            categoria: formData.serviceType,
          }
        ])
        .select()

      if (error) {
        console.error('Error insertando proveedor:', error)
        
        // Manejar error de email duplicado
        if (error.code === '23505') {
          setFormStatus({
            submitted: false,
            error: true,
            message: "Ya existe un proveedor registrado con ese email.",
            loading: false,
          })
        } else {
          setFormStatus({
            submitted: false,
            error: true,
            message: "Error al registrar proveedor: " + error.message,
            loading: false,
          })
        }
        return
      }

      // Éxito
      setFormStatus({
        submitted: true,
        error: false,
        message: "¡Proveedor registrado exitosamente! Pronto nos pondremos en contacto.",
        loading: false,
      })

      console.log("Nuevo proveedor registrado:", data[0])

      // Resetear el formulario
      setFormData({
        providerName: "",
        email: "",
        phone: "",
        serviceType: "",
        portfolio: null,
        notes: "",
      })

      // Limpiar el mensaje después de 5 segundos
      setTimeout(() => {
        setFormStatus({
          submitted: false,
          error: false,
          message: "",
          loading: false,
        })
      }, 5000)

    } catch (error) {
      console.error('Error al guardar proveedor:', error)
      setFormStatus({
        submitted: false,
        error: true,
        message: "Error de conexión. Verifica tu internet e intenta nuevamente.",
        loading: false,
      })
    }
  }

  return (
    <>
    <section className="hiring">
      <div className="hiring__container">
        <div className="hiring__header">
          <Users className="hiring__icon" size={40} />
          <h2 className="hiring__title">Hagamos Equipo</h2>
          <p className="hiring__subtitle">Registrese como colaborador para nuestra empresa</p>
        </div>

        <div className="hiring__application">
          <form className="hiring__form" onSubmit={handleSubmit}>
            <h3 className="hiring__form-title">Datos del Proveedor</h3>

            {formStatus.submitted && <div className="hiring__form-success">{formStatus.message}</div>}
            {formStatus.error && <div className="hiring__form-error">{formStatus.message}</div>}

            <div className="hiring__form-group">
              <label htmlFor="providerName" className="hiring__form-label">
                Nombre Completo <span className="hiring__required">*</span>
              </label>
              <input
                type="text"
                id="providerName"
                name="providerName"
                value={formData.providerName}
                onChange={handleChange}
                className="hiring__form-input"
                required
                disabled={formStatus.loading}
              />
            </div>

            <div className="hiring__form-group">
              <label htmlFor="email" className="hiring__form-label">
                Email <span className="hiring__required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="hiring__form-input"
                required
                disabled={formStatus.loading}
              />
            </div>

            <div className="hiring__form-group">
              <label htmlFor="phone" className="hiring__form-label">
                Teléfono
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="hiring__form-input"
                disabled={formStatus.loading}
              />
            </div>

            <div className="hiring__form-group">
              <label htmlFor="serviceType" className="hiring__form-label">
                Tipo de Servicio <span className="hiring__required">*</span>
              </label>
              <select
                id="serviceType"
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                className="hiring__form-select"
                required
                disabled={formStatus.loading}
              >
                <option value="">Selecciona un servicio</option>
                <option value="maquillaje">Maquillaje</option>
                <option value="dj">Dj y locución</option>
                <option value="peinado">Peinado</option>
                <option value="glitter">Glitter</option>
                <option value="deco">Decoración</option>
                <option value="show">Técnica y show</option>
                <option value="mesa_dulce">Mesa Dulce</option>
                <option value="vestidos">Vestidos</option>
                <option value="evento">Org. de eventos</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div className="hiring__form-group">
              <label htmlFor="portfolio" className="hiring__form-label">
                Portafolio (PDF) 
              </label>
              <div className="hiring__file-upload">
                <input
                  type="file"
                  id="portfolio"
                  name="portfolio"
                  onChange={handleFileChange}
                  className="hiring__file-input"
                  accept=".pdf,.doc,.docx"
                  disabled={formStatus.loading}
                />
                <div className="hiring__file-button">
                  {formData.portfolio ? formData.portfolio.name : "Seleccionar archivo"}
                </div>
              </div>
            </div>

            <div className="hiring__form-group">
              <label htmlFor="notes" className="hiring__form-label">
                Notas Adicionales
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="hiring__form-textarea"
                rows={4}
                placeholder="Información extra sobre el proveedor, dirección, etc."
                disabled={formStatus.loading}
              ></textarea>
            </div>

            <button 
              type="submit" 
              className="hiring__form-button"
              disabled={formStatus.loading}
            >
              {formStatus.loading ? "Registrando..." : "Quiero ser proveedor"}
            </button>
          </form>
        </div>
      </div>
    </section>
    <Footer />
    </>
  )
}